import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getNearestNodeFromDOMNode, isDOMNode } from 'lexical';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { FieldRefTarget } from '../../util/formula-vocabulary';
import { $tokenAtNode, DocPopup, renderHintDoc, resolveDoc } from './tokenDocs';

/** How long the pointer rests on a token before its doc shows. */
export const HOVER_DOC_DELAY_MS = 300;

/** Grace period to move the pointer from the token into the popup. */
const HOVER_DOC_HIDE_DELAY_MS = 150;

interface HoverDocPluginProps {
  fields: FieldRefTarget[];
  /** While true (a typeahead or hint menu is open), hovering shows nothing and an open popup closes. */
  suppressed?: boolean;
}

/**
 * IDE-style quick docs: resting the pointer on a token (a field chip, a
 * function name) pops the same doc panel the typeahead side panel shows.
 * Entering the popup keeps it; any edit, or moving off the token, hides it.
 */
export function HoverDocPlugin({ fields, suppressed = false }: HoverDocPluginProps) {
  const [editor] = useLexicalComposerContext();
  const [popup, setPopup] = useState<{ content: ReactNode; rect: DOMRect; nodeKey: string } | null>(null);
  const popupRef = useRef(popup);
  popupRef.current = popup;
  const showTimerRef = useRef<number | undefined>(undefined);
  const hideTimerRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    const cancelShow = () => window.clearTimeout(showTimerRef.current);
    const cancelHide = () => window.clearTimeout(hideTimerRef.current);
    const scheduleHide = () => {
      cancelHide();
      hideTimerRef.current = window.setTimeout(() => setPopup(null), HOVER_DOC_HIDE_DELAY_MS);
    };

    const onMouseOver = (event: MouseEvent) => {
      cancelShow();
      if (suppressed || !isDOMNode(event.target)) {
        return;
      }
      const domNode = event.target;
      // editor.read, not getEditorState().read: $getNearestNodeFromDOMNode
      // needs the active EDITOR (its DOM map), not just an active state.
      const found = editor.read(() => {
        const token = $tokenAtNode($getNearestNodeFromDOMNode(domNode));
        const doc = token && resolveDoc(token, fields);
        return token && doc ? { doc, nodeKey: token.nodeKey } : null;
      });
      if (!found) {
        scheduleHide();
        return;
      }
      cancelHide();
      if (popupRef.current?.nodeKey === found.nodeKey) {
        return;
      }
      showTimerRef.current = window.setTimeout(() => {
        const rect = editor.getElementByKey(found.nodeKey)?.getBoundingClientRect();
        if (rect) {
          setPopup({ content: renderHintDoc(found.doc), rect, nodeKey: found.nodeKey });
        }
      }, HOVER_DOC_DELAY_MS);
    };
    const onMouseLeave = () => {
      cancelShow();
      scheduleHide();
    };

    // The root element may mount after this effect (or be swapped); attach
    // wherever it currently is. The teardown call (null root) detaches.
    const unregister = editor.registerRootListener((rootElement, prevRootElement) => {
      prevRootElement?.removeEventListener('mouseover', onMouseOver);
      prevRootElement?.removeEventListener('mouseleave', onMouseLeave);
      // eslint-disable-next-line @eslint-react/web-api/no-leaked-event-listener -- unregister()'s teardown calls back with (null, root), removing these
      rootElement?.addEventListener('mouseover', onMouseOver);
      // eslint-disable-next-line @eslint-react/web-api/no-leaked-event-listener -- see above
      rootElement?.addEventListener('mouseleave', onMouseLeave);
    });
    return () => {
      cancelShow();
      cancelHide();
      unregister();
    };
  }, [editor, fields, suppressed]);

  // A menu opening over an already-visible popup closes it (the mouse
  // handlers only gate NEW popups; this enforces the invariant reactively).
  useEffect(() => {
    if (suppressed) {
      window.clearTimeout(showTimerRef.current);
      setPopup(null);
    }
  }, [suppressed]);

  // Any edit or caret change invalidates the doc's anchor; hide immediately.
  useEffect(() => editor.registerUpdateListener(() => setPopup(null)), [editor]);

  if (!popup) {
    return null;
  }
  return (
    <DocPopup
      rect={popup.rect}
      onMouseEnter={() => window.clearTimeout(hideTimerRef.current)}
      onMouseLeave={() => setPopup(null)}
    >
      {popup.content}
    </DocPopup>
  );
}
